import React, { useState, useEffect, useCallback } from 'react';
import { reviewsAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Star, Check, X, Trash2, Eye, EyeOff } from 'lucide-react';

const ReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('');

  const loadReviews = useCallback(async () => {
    try {
      const { data } = await reviewsAPI.getAll(filter || undefined);
      setReviews(data);
    } catch (_) {
      toast.error('Failed to load reviews');
    }
  }, [filter]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  const handleStatus = async (reviewId, status) => {
    try {
      await reviewsAPI.updateStatus(reviewId, status);
      toast.success(`Review ${status}`);
      loadReviews();
    } catch (_) { toast.error('Failed to update review'); }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Permanently delete this review?')) return;
    try {
      await reviewsAPI.delete(reviewId);
      toast.success('Review deleted');
      loadReviews();
    } catch (_) { toast.error('Failed to delete review'); }
  };

  const statusColors = {
    approved: 'bg-[#3A5A40]/10 text-[#3A5A40]',
    rejected: 'bg-[#9E2A2B]/10 text-[#9E2A2B]',
    hidden: 'bg-[#8C8577]/10 text-[#8C8577]',
  };

  return (
    <div data-testid="reviews-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-cormorant text-2xl">Review Moderation</h2>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-[#EBE4D5] rounded-md px-3 py-1.5 text-sm font-outfit"
          data-testid="review-status-filter"
        >
          <option value="">All Reviews</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="hidden">Hidden</option>
        </select>
      </div>

      {reviews.length === 0 ? (
        <p className="font-outfit text-[#8C8577] text-center py-12">No reviews found</p>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white p-5 rounded-lg shadow-sm" data-testid={`admin-review-${review.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#C29B57] text-[#C29B57]' : 'text-[#EBE4D5]'}`} />
                      ))}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-outfit ${statusColors[review.status] || ''}`}>
                      {review.status}
                    </span>
                    {review.is_verified_purchase && (
                      <span className="text-xs text-[#3A5A40] font-outfit">Verified Purchase</span>
                    )}
                  </div>
                  <p className="font-outfit text-sm font-medium">{review.customer_name}</p>
                  <p className="font-outfit text-xs text-[#8C8577]">{review.customer_email} · {new Date(review.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <p className="font-outfit text-xs text-[#8C8577]">Product: {review.product_id.slice(0, 8)}...</p>
              </div>

              {review.title && <p className="font-outfit text-sm font-medium mb-1">{review.title}</p>}
              <p className="font-outfit text-sm text-[#4A463F] mb-3">{review.text}</p>

              {/* Media */}
              {(review.photos?.length > 0 || review.videos?.length > 0) && (
                <div className="flex gap-2 flex-wrap mb-3">
                  {review.photos?.map((p) => (
                    <div key={p} className="w-12 h-12 rounded overflow-hidden border border-[#EBE4D5]">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {review.videos?.map((v) => (
                    <div key={v} className="w-12 h-12 rounded overflow-hidden border border-[#EBE4D5] bg-[#1A1A18] flex items-center justify-center text-white text-xs">VID</div>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-3 border-t border-[#EBE4D5]">
                {review.status !== 'approved' && (
                  <button onClick={() => handleStatus(review.id, 'approved')} className="flex items-center gap-1 px-3 py-1.5 bg-[#3A5A40]/10 text-[#3A5A40] rounded-md text-xs font-outfit hover:bg-[#3A5A40]/20 transition-colors" data-testid={`approve-${review.id}`}>
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                )}
                {review.status !== 'rejected' && (
                  <button onClick={() => handleStatus(review.id, 'rejected')} className="flex items-center gap-1 px-3 py-1.5 bg-[#9E2A2B]/10 text-[#9E2A2B] rounded-md text-xs font-outfit hover:bg-[#9E2A2B]/20 transition-colors" data-testid={`reject-${review.id}`}>
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                )}
                {review.status !== 'hidden' && (
                  <button onClick={() => handleStatus(review.id, 'hidden')} className="flex items-center gap-1 px-3 py-1.5 bg-[#8C8577]/10 text-[#8C8577] rounded-md text-xs font-outfit hover:bg-[#8C8577]/20 transition-colors" data-testid={`hide-${review.id}`}>
                    <EyeOff className="w-3.5 h-3.5" /> Hide
                  </button>
                )}
                <button onClick={() => handleDelete(review.id)} className="flex items-center gap-1 px-3 py-1.5 bg-[#9E2A2B]/10 text-[#9E2A2B] rounded-md text-xs font-outfit hover:bg-[#9E2A2B]/20 transition-colors ml-auto" data-testid={`delete-review-${review.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewManagement;
